import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAccessToken } from '../utils/auth';
import { getUnresolvedAircraftPhotos, applyAircraftTypeResolutions } from '../utils/httpApi';
import { isApiSuccess, getFirstApiError } from '../utils/api';
import { parseAircraftTypeFromPhotoUrl } from '../utils/aircraftTypeFromPhoto';
import AdminSearchSelect from './AdminSearchSelect';
import './Admin.css';
import './AdminReferenceData.css';

const RECENT_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '', label: 'All time' }
];

const VIEW_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'suggested', label: 'Suggested' },
  { value: 'manual', label: 'Needs manual' },
  // Photo present but the slug did not parse: these are PARSER gaps, not
  // missing data, and each one is a rule that can be added. Kept separate from
  // "Needs manual", which also contains aircraft that simply have no photo.
  { value: 'unparsed', label: 'Photo, no match' }
];

const SUCCESS_STATUSES = new Set(['APPLIED', 'ALIASED']);
const APPLY_CHUNK = 50;
const PAGE_LIMIT = 200;
const MAX_FETCH = 20;
// Successful rows stay visible briefly (fading) so the result badge is seen.
const FADE_OUT_MS = 1500;

/**
 * The backend's registry suggestion may be a bare designator or an object.
 * Normalise to `{typeDesignator, manufacturer, model, source:'registry'}`.
 */
export function normaliseRegistrySuggestion(s) {
  if (!s) return null;
  if (typeof s === 'string') {
    return s.trim() ? { typeDesignator: s.trim().toUpperCase(), manufacturer: '', model: '', source: 'registry' } : null;
  }
  const typeDesignator = s.typeDesignator || s.type_designator || s.icaoTypeDesignator || s.icao_type_designator;
  if (!typeDesignator) return null;
  return {
    typeDesignator: String(typeDesignator).toUpperCase(),
    manufacturer: s.manufacturer || '',
    model: s.model || '',
    source: 'registry'
  };
}

/** Registry first, then the photo-slug parser; null when neither can say. */
export function suggestionFor(row) {
  const fromRegistry = normaliseRegistrySuggestion(row.registrySuggestion);
  if (fromRegistry) return fromRegistry;
  const parsed = row.photo && row.photo.imageUrl ? parseAircraftTypeFromPhotoUrl(row.photo.imageUrl) : null;
  if (!parsed) return null;
  return { typeDesignator: parsed.typeCode, manufacturer: parsed.manufacturer, model: parsed.model, source: 'parser' };
}

function photoSlug(row) {
  if (row.photo && row.photo.slug) return row.photo.slug;
  const url = row.photo && row.photo.imageUrl;
  if (!url) return '';
  const path = url.split('?')[0].replace(/\/+$/, '');
  return path.substring(path.lastIndexOf('/') + 1);
}

function statusClass(status) {
  return SUCCESS_STATUSES.has(status) ? 'admin-row-result-ok' : 'admin-row-result-error';
}

/**
 * Aircraft with no type, their Planespotters photo and a suggested designator
 * (registry or parsed from the photo slug). Admins apply suggestions singly
 * or in bulk; rows the parser cannot resolve are left for the
 * resolve-aircraft-types Claude Code skill.
 */
function AdminResolveTypes() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ totalCount: 0, remainingUncached: 0, fetchedThisCall: 0 });
  const [recentDays, setRecentDays] = useState('30');
  const [view, setView] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selections, setSelections] = useState({});
  const [results, setResults] = useState({});
  const [dismissed, setDismissed] = useState(() => new Set());
  const [applying, setApplying] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = { limit: PAGE_LIMIT, fetchMissing: true, maxFetch: MAX_FETCH };
      if (recentDays) payload.recentDays = Number(recentDays);
      const resp = await getUnresolvedAircraftPhotos(payload, getAccessToken());
      if (!isApiSuccess(resp)) {
        setError(getFirstApiError(resp) || 'Failed to load unresolved aircraft');
        return;
      }
      const loaded = resp.rows || [];
      setRows(loaded);
      setMeta({
        totalCount: resp.totalCount ?? loaded.length,
        remainingUncached: resp.remainingUncached ?? 0,
        fetchedThisCall: resp.fetchedThisCall ?? 0
      });
      setResults({});
      setDismissed(new Set());
      // Prefill the picker with the suggestion for every row that has one.
      const next = {};
      loaded.forEach(row => {
        const s = suggestionFor(row);
        if (s) next[row.icao24] = s;
      });
      setSelections(next);
    } catch (e) {
      setError('Failed to load unresolved aircraft');
    } finally {
      setLoading(false);
    }
  }, [recentDays]);

  useEffect(() => { load(); }, [load]);

  const suggestions = useMemo(() => {
    const out = {};
    rows.forEach(row => { out[row.icao24] = suggestionFor(row); });
    return out;
  }, [rows]);

  const visibleRows = useMemo(() => rows.filter(row => {
    if (dismissed.has(row.icao24)) return false;
    if (view === 'suggested') return !!suggestions[row.icao24];
    if (view === 'manual') return !suggestions[row.icao24];
    if (view === 'unparsed') return !suggestions[row.icao24] && !!(row.photo && row.photo.imageUrl);
    return true;
  }), [rows, view, suggestions, dismissed]);

  // Every aircraft with a photo the parser could not read. Derived, so it is
  // always current and empties itself as rules are added — nothing to reconcile.
  const unparsedRows = useMemo(
    () => rows.filter(r => !suggestions[r.icao24] && r.photo && r.photo.imageUrl),
    [rows, suggestions]
  );

  // Tab-separated so the list pastes straight into a sheet or a ticket.
  const TAB = String.fromCharCode(9);
  const NEWLINE = String.fromCharCode(10);

  const copyUnparsed = async () => {
    const lines = unparsedRows.map(r =>
      [r.icao24, r.registration || '-', photoSlug(r), r.photo.imageUrl].join(TAB));
    const text = [['icao24','registration','slug','photo_url'].join(TAB), ...lines].join(NEWLINE);
    try {
      await navigator.clipboard.writeText(text);
      setActionMsg(`Copied ${unparsedRows.length} unmatched photo rows`);
    } catch (e) {
      setActionMsg('Copy failed — clipboard unavailable');
    }
  };

  const suggestedCount = useMemo(
    () => rows.filter(r => suggestions[r.icao24] && !(results[r.icao24] && SUCCESS_STATUSES.has(results[r.icao24].status))).length,
    [rows, suggestions, results]
  );

  const setSelection = (icao24, opt) => {
    setSelections(prev => {
      const next = { ...prev };
      if (!opt) {
        delete next[icao24];
      } else {
        // Manufacturer/model are left for the backend (fillFromType) — the
        // type row only carries a combined manufacturer_model string.
        next[icao24] = { typeDesignator: opt.value, manufacturer: '', model: '', source: 'manual' };
      }
      return next;
    });
  };

  const applyResolutions = async (resolutions) => {
    if (resolutions.length === 0) return;
    const noun = resolutions.length === 1 ? `type ${resolutions[0].typeDesignator} to ${resolutions[0].icao24}` : `${resolutions.length} aircraft types`;
    if (!window.confirm(`Apply ${noun}?`)) return;
    setApplying(true);
    setActionMsg(null);
    let applied = 0;
    let failed = 0;
    try {
      const token = getAccessToken();
      for (let i = 0; i < resolutions.length; i += APPLY_CHUNK) {
        const chunk = resolutions.slice(i, i + APPLY_CHUNK);
        const resp = await applyAircraftTypeResolutions({ resolutions: chunk, dryRun: false }, token);
        if (!isApiSuccess(resp)) {
          setActionMsg({ type: 'error', text: getFirstApiError(resp) || 'Apply failed' });
          failed += chunk.length;
          break;
        }
        const byHex = {};
        (resp.results || []).forEach(r => { byHex[r.icao24] = r; });
        setResults(prev => ({ ...prev, ...byHex }));
        const okHexes = Object.values(byHex).filter(r => SUCCESS_STATUSES.has(r.status)).map(r => r.icao24);
        applied += okHexes.length;
        failed += chunk.length - okHexes.length;
        if (okHexes.length) {
          setTimeout(() => setDismissed(prev => new Set([...prev, ...okHexes])), FADE_OUT_MS);
        }
      }
      if (applied || failed) {
        setActionMsg({
          type: failed ? 'error' : 'success',
          text: `${applied} applied${failed ? `, ${failed} not applied` : ''}`
        });
      }
    } catch (e) {
      setActionMsg({ type: 'error', text: 'Apply failed' });
    } finally {
      setApplying(false);
    }
  };

  const resolutionFor = (icao24) => {
    const sel = selections[icao24];
    if (!sel || !sel.typeDesignator) return null;
    return {
      icao24,
      typeDesignator: sel.typeDesignator,
      manufacturer: sel.manufacturer || undefined,
      model: sel.model || undefined,
      source: sel.source
    };
  };

  const applyRow = (icao24) => {
    const r = resolutionFor(icao24);
    if (r) applyResolutions([r]);
  };

  const applyAllSuggested = () => {
    const list = rows
      .filter(row => suggestions[row.icao24] && !(results[row.icao24] && SUCCESS_STATUSES.has(results[row.icao24].status)))
      .map(row => resolutionFor(row.icao24))
      .filter(Boolean);
    applyResolutions(list);
  };

  return (
    <div className="admin-resolve-types">
      <div className="admin-toolbar">
        <label>
          Seen
          <select value={recentDays} onChange={e => setRecentDays(e.target.value)} disabled={loading}>
            {RECENT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <div className="admin-status-filter" role="group" aria-label="View">
          {VIEW_OPTIONS.map(o => (
            <button
              key={o.value}
              type="button"
              className={`admin-filter-chip${view === o.value ? ' active' : ''}`}
              aria-pressed={view === o.value}
              onClick={() => setView(o.value)}
            >
              {o.label}
            </button>
          ))}
        </div>
        <button type="button" onClick={load} disabled={loading}>Refresh</button>
        <button type="button" onClick={applyAllSuggested} disabled={applying || loading || suggestedCount === 0}>
          Apply all suggested ({suggestedCount})
        </button>
        {unparsedRows.length > 0 && (
          <button type="button" onClick={copyUnparsed} disabled={loading}>
            Copy unmatched photos ({unparsedRows.length})
          </button>
        )}
        {meta.remainingUncached > 0 && (
          <button type="button" onClick={load} disabled={loading}>
            Fetch more photos ({meta.remainingUncached} uncached)
          </button>
        )}
        <span className="admin-toolbar-meta">
          {loading ? 'Loading…' : `${visibleRows.length} shown of ${meta.totalCount} without a type`}
        </span>
      </div>

      <div className="admin-info-note">
        <strong>Needs manual</strong> has no registry entry and nothing usable from a photo.
        <strong> Photo, no match</strong> is the subset that DOES have a photo whose slug the parser could not
        read — each is a missing rule in <code>utils/aircraftTypeFromPhoto.js</code>, so copy the list and add
        them. Both are also handled by the <code>resolve-aircraft-types</code> Claude Code skill.
      </div>

      {actionMsg && (
        <div className={`admin-action-message admin-action-${actionMsg.type}`} role="status">
          {actionMsg.text}
        </div>
      )}

      {error && (
        <div className="admin-error">
          <p>{error}</p>
          <button type="button" onClick={load}>Retry</button>
        </div>
      )}

      {!error && (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Hex</th>
                <th>Reg</th>
                <th>Callsign / operator</th>
                <th>Photo</th>
                <th>Suggested</th>
                <th>Type</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {!loading && visibleRows.length === 0 && (
                <tr><td colSpan={7} className="unknown-empty">Nothing to resolve</td></tr>
              )}
              {visibleRows.map(row => {
                const suggestion = suggestions[row.icao24];
                const selection = selections[row.icao24];
                const result = results[row.icao24];
                const slug = photoSlug(row);
                const photoUrl = row.photo && row.photo.imageUrl;
                const thumb = row.photo && (row.photo.thumbnailUrl || row.photo.largeThumbnailUrl);
                const done = result && SUCCESS_STATUSES.has(result.status);
                return (
                  <tr key={row.icao24} className={done ? 'admin-row-done' : undefined}>
                    <td data-label="Hex" className="admin-ref-mono">{row.icao24}</td>
                    <td data-label="Reg" className="admin-ref-mono">{row.registration || <span className="gap">—</span>}</td>
                    <td data-label="Callsign">
                      <div>{row.callsign || <span className="gap">—</span>}</div>
                      <div className="unknown-secondary">{row.operator || ''}</div>
                    </td>
                    <td data-label="Photo">
                      {photoUrl ? (
                        <a href={photoUrl} target="_blank" rel="noopener noreferrer" className="admin-resolve-photo">
                          {thumb ? <img className="admin-resolve-thumb" src={thumb} alt="" loading="lazy" /> : 'Photo'}
                          <span className="admin-resolve-slug">{slug}</span>
                        </a>
                      ) : (
                        <span className="admin-status-badge inactive">{row.photoStatus || 'No photo'}</span>
                      )}
                    </td>
                    <td data-label="Suggested">
                      {suggestion ? (
                        <span className="admin-status-badge active" title={suggestion.source === 'registry' ? 'From aircraft registry' : 'Parsed from photo slug'}>
                          {suggestion.typeDesignator}
                          {suggestion.manufacturer ? ` · ${suggestion.manufacturer} ${suggestion.model}`.trimEnd() : ''}
                        </span>
                      ) : (
                        <span className="admin-status-badge inactive">Needs manual</span>
                      )}
                    </td>
                    <td data-label="Type">
                      <AdminSearchSelect
                        value={selection ? selection.typeDesignator : ''}
                        ariaLabel={`Type for ${row.icao24}`}
                        onSelect={opt => setSelection(row.icao24, opt)}
                        disabled={applying}
                      />
                    </td>
                    <td data-label-none>
                      <div className="admin-ref-actions">
                        <button type="button" onClick={() => applyRow(row.icao24)} disabled={applying || !selection}>
                          Apply
                        </button>
                        {result && (
                          <span className={`admin-status-badge ${statusClass(result.status)}`} title={result.reason || ''}>
                            {result.status}{result.canonical && result.canonical !== result.requested ? ` → ${result.canonical}` : ''}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminResolveTypes;
